<template>
    <page-sidebar class="md:col-span-4" :links="links" :bold="false"></page-sidebar>
</template>

<script setup>
import { computed, inject } from 'vue';
import {
    faBadgePercent,
    faBookAtlas,
    faBuildingFlag,
    faCalendarCirclePlus,
    faCircleInfo,
    faCircleUser,
    faContactBook,
    faCreditCard,
    faEye,
    faFingerprint,
    faFlagCheckered,
    faGrid2Plus,
    faKeySkeletonLeftRight,
    faWandMagicSparkles,
    faListUl,
    faLockKeyhole,
    faMemoCircleInfo,
    faMoneyCheckDollarPen,
    faPhone,
    faPhoneOffice,
    faPuzzlePiece,
    faRectangleHistoryCirclePlus,
    faRectangleHistoryCircleUser,
    faRefresh,
    faRightToBracket,
    faShoppingCart,
    faUserPlus,
    faUsers,
    faPhoneArrowRight,
    faCalendarAlt,
} from '@fortawesome/pro-solid-svg-icons';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { match } from 'path-to-regexp';
import { faHubspot, faSalesforce, faShopify } from '@fortawesome/free-brands-svg-icons';

const usesApi = Spark.usesApi;
const isTeamOwner = Spark.ownsTeam;
const isAdminOrOwner = Spark.isAdminOrOwner;
const isImpersonatorOrPlatformAdmin = Spark.isImpersonating || Spark.isPlatformAdmin;
const isServiceRepresentative = Spark.isServiceRepresentative;

const user = Spark.state.user;

const teamId = Spark.state.currentTeam.id;
const parentId = Spark.state.currentTeam.parent_id;
const isBillable = !parentId || Spark.state.currentTeam.is_billable;
const teamStatus = Spark.state.currentTeam.status;

const FEATURES = inject('FEATURES');

const { onCorporate, roleOnCurrentGroup, currentGroup, roleOnCurrentOrgUnit, currentOrgUnit } = useCurrentGroup();

const links = computed(() => {
    const yourSettings = {
        label: 'User Settings',
        links: [
            {
                href: `/settings#/profile`,
                label: 'Profile',
                icon: faCircleUser,
            },
        ],
    };

    yourSettings.links.push({
        href: `/settings#/teams`,
        label: 'Teams',
        icon: faUsers,
    });

    if (!user?.isUsingSingleSignOn) {
        yourSettings.links.push({
            href: `/settings#/invites`,
            label: 'Invites',
            icon: faUserPlus,
        });

        yourSettings.links.push({
            href: `/settings#/security`,
            label: 'Security',
            icon: faLockKeyhole,
        });
    }

    if (onCorporate() && usesApi && !user?.isUsingSingleSignOn) {
        yourSettings.links.push({
            href: `/settings#/api`,
            label: 'API Tokens',
            icon: faKeySkeletonLeftRight,
        });
    }

    const allLinks = [yourSettings];

    if (onCorporate()) {
        const teamSettings = {
            label: 'Team Settings',
            links: [],
        };

        teamSettings.links.push({
            href: `/settings/teams/${teamId}#/owner`,
            label: 'Team Profile',
            icon: faCircleUser,
        });

        if (isTeamOwner && !parentId) {
            if (FEATURES.single_sign_on) {
                teamSettings.links.push({
                    href: `/settings/teams/${teamId}#/sso-settings`,
                    label: 'Account SSO',
                    icon: faRightToBracket,
                });
            }
        }

        if (!user?.isUsingSingleSignOn) {
            teamSettings.links.push({
                href: `/settings/teams/${teamId}#/membership`,
                label: isAdminOrOwner ? 'Users' : 'Membership',
                icon: faUsers,
            });
        }

        if (!isServiceRepresentative) {
            teamSettings.links.push({
                href: `/settings/teams/${teamId}#/phone-numbers`,
                label: 'Phone Numbers',
                icon: faPhone,
            });
        }

        if (!isServiceRepresentative) {
            teamSettings.links.push({
                href: `/settings/teams/${teamId}#/phone-number-pools`,
                label: 'Phone Pools',
                icon: faPhoneOffice,
            });
        }

        teamSettings.links.push({
            href: `/settings/teams/${teamId}#/customattributes`,
            label: 'Custom Attributes',
            icon: faPuzzlePiece,
        });

        teamSettings.links.push({
            href: `/settings/teams/${teamId}#/contacts-exports`,
            label: 'Contacts Exports',
            icon: faContactBook,
        });

        allLinks.push(teamSettings);
    }

    if (onCorporate() && FEATURES.contact_groups) {
        const franchiseHub = {
            label: 'Franchise Hub',
            links: [
                {
                    href: `/settings/teams/${teamId}#/analytics-goals`,
                    label: 'Analytics Goals',
                    icon: faFlagCheckered,
                },
                {
                    href: `/settings/teams/${teamId}#/message-standards`,
                    label: 'Brand Standards',
                    icon: faListUl,
                },
                {
                    href: `/settings/teams/${teamId}#/org-units`,
                    label: 'OUs',
                    icon: faBuildingFlag,
                    pathMatcher: match(['/settings/teams/:teamId#/org-units{/*path}']),
                },
                {
                    href: `/settings/teams/${teamId}#/groups`,
                    label: 'Groups',
                    icon: faGrid2Plus,
                },
                {
                    href: `/settings/teams/${teamId}#/group-collections`,
                    label: 'Collections',
                    icon: faRectangleHistoryCirclePlus,
                },
                {
                    href: `/settings/teams/${teamId}#/cost-visibility`,
                    label: 'Cost Visibility',
                    icon: faEye,
                },
            ],
        };

        allLinks.push(franchiseHub);
    }

    if (onCorporate() && isAdminOrOwner) {
        const integrations = {
            label: 'Integrations',
            links: [
                {
                    href: '/settings/integrations#/shopify',
                    label: 'Shopify',
                    icon: faShopify,
                },
                {
                    href: '/settings/integrations#/contact-collector',
                    label: 'Contact Collector',
                    icon: faRectangleHistoryCircleUser,
                },
            ],
        };

        if (!isServiceRepresentative) {
            integrations.links.push({
                href: '/settings/integrations/promotions',
                label: 'Promotions',
                icon: faBadgePercent,
            });
        }

        if (FEATURES.salesforce_integration) {
            integrations.links.push({
                href: '/settings/integrations#/salesforce',
                label: 'Salesforce',
                icon: faSalesforce,
            });
        }

        if (FEATURES.hubspot_integration) {
            integrations.links.push({
                href: '/settings/integrations#/hubspot',
                label: 'HubSpot',
                icon: faHubspot,
            });
        }

        if (FEATURES.calendly_integration) {
            integrations.links.push({
                href: '/settings/integrations#/calendly',
                label: 'Calendly',
                icon: faCalendarCirclePlus,
            });
        }

        if (FEATURES.acuity_integration) {
            integrations.links.push({
                href: '/settings/integrations#/acuity',
                label: 'Acuity',
                icon: faCalendarAlt,
            });
        }

        if (FEATURES.message_hub_ai || FEATURES.quick_blast_ai) {
            integrations.links.push({
                href: '/settings/integrations#/ai-integration',
                label: 'AI Configuration',
                icon: faWandMagicSparkles,
            });
        }

        if (FEATURES.api_destinations) {
            integrations.links.push({
                href: '/settings/integrations#/auth-configs',
                label: 'API Auth',
                icon: faFingerprint,
            });

            integrations.links.push({
                href: '/settings/integrations#/api-destinations',
                label: 'API Destinations',
                icon: faBookAtlas,
            });
        }

        if (FEATURES.shift_to_text) {
            integrations.links.push({
                href: `/settings/teams/${teamId}#/shift-to-text`,
                label: 'Shift to Text',
                icon: faPhoneArrowRight,
            });
        }

        allLinks.push(integrations);

        const billing = {
            label: 'Billing',
            links: [],
        };

        if (FEATURES.new_billing_implementation) {
            if (isBillable) {
                billing.links.push({
                    href: `/settings/billing#/billing-details`,
                    label: 'Billing Details',
                    icon: faCreditCard,
                });

                if (teamStatus !== 'closed') {
                    billing.links.push({
                        href: `/settings/billing#/proforma-invoices`,
                        label: 'Proforma Invoices',
                        icon: faMoneyCheckDollarPen,
                    });
                }

                billing.links.push({
                    href: `/settings/billing#/historical-invoices`,
                    label: 'Historical Invoices',
                    icon: faShoppingCart,
                });
            }
        } else {
            billing.links = [
                {
                    href: `/settings/teams/${teamId}#/payment-information`,
                    label: 'Payment Information',
                    icon: faCreditCard,
                },
                {
                    href: `/settings/teams/${teamId}#/invoices`,
                    label: 'Invoices',
                    icon: faShoppingCart,
                },
            ];

            if (isImpersonatorOrPlatformAdmin) {
                billing.links.unshift({
                    href: `/settings/teams/${teamId}#/subscription`,
                    label: 'Subscription',
                    icon: faRefresh,
                });
            }

            billing.links.unshift({
                href: `/settings/teams/${teamId}#/plan-information`,
                label: 'Plan Information',
                icon: faMemoCircleInfo,
            });
        }

        if (billing.links.length > 0) {
            allLinks.push(billing);
        }
    }

    if (currentOrgUnit.value?.id) {
        const orgUnitSettings = {
            label: 'OU Settings',
            links: [],
        };

        orgUnitSettings.links.push({
            href: `/settings/teams/${teamId}#/current-org-unit-details`,
            label: 'Details',
            icon: faCircleInfo,
        });

        if (roleOnCurrentOrgUnit === 'admin') {
            orgUnitSettings.links.push({
                href: `/settings/teams/${teamId}#/current-org-unit-users`,
                label: 'Users',
                icon: faUsers,
            });

            orgUnitSettings.links.push({
                href: `/settings/teams/${teamId}#/current-org-unit-groups`,
                label: 'Groups',
                icon: faGrid2Plus,
            });
        }

        orgUnitSettings.links.push({
            href: `/settings/teams/${teamId}#/current-org-unit-collections`,
            label: 'Collections',
            icon: faRectangleHistoryCirclePlus,
        });

        allLinks.push(orgUnitSettings);

        // Add billing section if team has new billing implementation and org unit is billable
        if (
            FEATURES.new_billing_implementation &&
            currentOrgUnit.value?.is_billable &&
            roleOnCurrentOrgUnit === 'admin'
        ) {
            const ouBilling = {
                label: 'Billing',
                links: [
                    {
                        href: `/settings/org-unit-billing#/billing-details`,
                        label: 'Billing Details',
                        icon: faCreditCard,
                    },
                    {
                        href: `/settings/org-unit-billing#/proforma-invoices`,
                        label: 'Proforma Invoices',
                        icon: faMoneyCheckDollarPen,
                    },
                    {
                        href: `/settings/org-unit-billing#/historical-invoices`,
                        label: 'Historical Invoices',
                        icon: faShoppingCart,
                    },
                ],
            };
            allLinks.push(ouBilling);
        }
    }

    if (currentGroup.value?.group_id) {
        const groupSettings = {
            label: 'Group Settings',
            links: [],
        };

        groupSettings.links.push({
            href: `/settings/teams/${teamId}#/group-details`,
            label: 'Details',
            icon: faCircleInfo,
        });

        if (['admin'].includes(roleOnCurrentGroup)) {
            groupSettings.links.push({
                href: `/settings/teams/${teamId}#/group-users`,
                label: 'Users',
                icon: faUsers,
            });
        }

        allLinks.push(groupSettings);
    }

    return allLinks;
});
</script>
